import { STORY_SCENE_IDS } from "../../src/game/story";
import { NO_CONSUMABLES } from "../../src/game/Consumable";
import { GameState } from "../../src/game/GameState";
import { ALL_ARTICLE_IDS } from "../../src/game/manual";
import { Board, BoardDimension } from "../../src/game/Materials";
import { MachineState } from "../../src/game/Machine";
import { STARTER_SKILLS } from "../../src/game/Skill";

function idleMachine(
  machineTypeId: MachineState["machineTypeId"],
  position: [number, number],
  selectedOperationId: string,
  selectedParameters?: MachineState["selectedParameters"],
): MachineState {
  return {
    machineTypeId,
    position,
    rotation: 0,
    inputMaterials: [],
    processingMaterials: [],
    outputMaterials: [],
    tools: [],
    selectedOperationId,
    selectedParameters,
    operationProgress: {
      status: "notStarted",
      phaseIndex: 0,
      minutesRemaining: 0,
    },
  };
}

/** Sanded S4S walnut frame stock, 1" wide and 1/4 thick. */
function frameStock(id: string, length: number): Board {
  return {
    id,
    type: "board",
    species: "walnut",
    length,
    width: 1,
    thickness: 1,
    surface: "sanded",
    jointedFaces: 2,
    jointedEdges: 2,
  };
}

/** A finished rail: mirrored 45° miters — the ends lean toward each other. */
function rail(id: string): Board {
  return {
    ...frameStock(id, 24),
    ends: {
      left: { kind: "mitered", angle: -45 },
      right: { kind: "mitered", angle: 45 },
    },
  };
}

/**
 * The picture frame chain, ready to run: a miter saw (op cell [2,4], where
 * the player starts) and a workspace (op cell [7,4]). One long stick of walnut
 * frame stock to cut the last rail from, three rails already mitered — one
 * carried, two piled mid-floor (the hands hold HAND_CAPACITY pieces, so the
 * spec ferries them) — nails in the drawer, and the miteredFrames skill
 * unlocked.
 */
export const miterFrameShop: GameState = {
  minute: 0,
  day: 1,
  dayStartMinute: 0,
  money: 1000,
  reputation: 10,
  consumables: { ...NO_CONSUMABLES, nails: 10 },
  clamps: 0,
  materialPiles: [
    { material: rail("test-rail-2"), position: [5.5, 4.5], rotation: 0 },
    { material: rail("test-rail-3"), position: [5.5, 4.5], rotation: 0 },
  ],
  player: {
    name: "Player",
    position: [2, 4], // the miter saw's operation cell
    direction: 0,
    inventory: [frameStock("test-stock-1", 96), rail("test-rail-1")],
    away: null,
  },
  machines: [
    idleMachine("miterSaw", [2, 2], "cutBoard", {
      angle: 0,
      cutPosition: 4,
    }),
    idleMachine("workspace", [7, 2], "buildPictureFrame"),
  ],
  machineCrates: [],
  truck: { bed: [], crates: [] },
  storage: {
    upgrades: [],
  },
  shopInfo: {
    name: "One Car Garage",
    electricity: 120,
    size: [12, 16],
    materialDropoffPosition: [10, 13],
    entrancePosition: [6, 15],
  },
  progression: {
    tutorials: {
      opening: { step: 0, dismissed: true },
      firstNight: { step: 1, dismissed: false },
      dust: { step: 0, dismissed: false },
    },
    storeUnlocked: true,
    standUnlocked: true,
    // A staged shop is past the opening: every scene has played
    story: { scenesPlayed: [...STORY_SCENE_IDS] },
    lumberyardUnlocked: false,
    salesCompleted: 5,
    sweepingUnlocked: false,
    unlockedArticles: ALL_ARTICLE_IDS,
    readArticles: ALL_ARTICLE_IDS,
    xp: 0,
    skillPoints: 0,
    unlockedSkills: [...STARTER_SKILLS, "miteredFrames"],
  },
  stand: [],
  standSetUp: true,
  customers: [],
  dust: {},
  shopVac: null,
  broomOwned: true,
  broomPosition: [0, 0],
  dustpan: {},
};
